import {SagaIterator} from 'redux-saga';
import {put, putResolve, call, select} from 'redux-saga/effects';

import {SUBSCRIPTION_LOCATION} from 'constants/analytics';

import {PAYMENTS, PRIMER_METHODS} from 'desktop/pages/checkout/constants/paymentStep';
import {managerNewsletterSubscription} from 'desktop/pages/checkout/utils/newsletterSubscription/managerNewsletterSubscription';
import {shouldBeAutoSubscribed} from 'desktop/pages/checkout/utils/newsletterSubscription/useSubscriptionInfo';
import {PayerInfoType} from 'desktop/types/payerInfoType';
import {PaymentInfoType} from 'desktop/types/PaymentInfoType';
import {getAffId} from 'libs/analytics/affiliate';

import {apiConfig} from 'libs/api/api';
import {isUhcEnabled} from 'libs/rollout';
import {localStore} from 'libs/storage/storage';

import {ActionType} from 'types/reducers';

import {completeOrderReviewPending, placeOrder} from '../../../actions/completeOrder/completeOrder';
import {getReviewData} from '../../../reducers/deliveryStep/customerAddresses/customerAddresses';
import {
    createTransitional,
    subscribe,
} from '../../../reducers/newsletterSubscription/newsletterSubscription';
import {fetchAmazonSessionInfo} from '../../../reducers/paymentStep/amazonV2/amazonV2';
import {setPayerInfo, setPayerOptions} from '../../../reducers/paymentStep/payerInfo/payerInfo';
import {savePayment} from '../../../reducers/paymentStep/savePaymentData/savePaymentData';

import {getCustomerAddresses} from '../../../selectors/deliveryStep/deliveryStep';
import {getEmailSubscriptionCheckboxStatus} from '../../../selectors/newsletterSubscription/newsletterSubscription';
import {getPayerInfo} from '../../../selectors/paymentStep/paymentStep';
import {setLoadingSavePaymentSaga} from '../setLoadingSavePaymentSaga/setLoadingSavePaymentSaga';

/**
 * Set payer info to store
 * @param {PaymentInfoType} payerInfoData - payer info
 */
export function* setPayerInfoSaga(payerInfoData: PayerInfoType): SagaIterator {
    const info = {...payerInfoData};

    // remove not prepare card
    if (info.cardNumber) {
        delete info.cardNumber;
    }

    // save payer info to store
    yield put(setPayerInfo(info));
}

/**
 * Send payer info to backend
 * @param {PaymentInfoType} data - payer info
 */
export function* savePaymentSaga(data: {
    payerInfoData: PaymentInfoType;
    formKey: string;
}): SagaIterator {
    try {
        const {formKey, payerInfoData} = data;

        yield putResolve(savePayment({formKey, paymentInfo: payerInfoData}));
    } catch (error) {
        yield call([console, 'log'], error);
    }
}

/**
 * Send payment info to backend & save to store
 * @param {PayerInfoType} payload - payer info
 */
export function* sendPaymentInfoSaga(payload: PayerInfoType): SagaIterator {
    const {form_key: formKey} = apiConfig.hostStore.getState().customer;
    const payerInfo = yield select(getPayerInfo);

    let subscribeStatus = payerInfo.options.subscribe;

    if (!isUhcEnabled()) {
        subscribeStatus = yield select(getEmailSubscriptionCheckboxStatus);
        yield put(setPayerOptions({subscribe: subscribeStatus}));
    }

    // send info to backend
    if (
        payload.method !== PAYMENTS.METHOD_CREDIT_CARD &&
        !PRIMER_METHODS.includes(payload.method as PAYMENTS)
    ) {
        yield call(savePaymentSaga, {
            formKey,
            payerInfoData: {...payload, ...payerInfo.options, subscribe: subscribeStatus},
        });
    }

    // save payer info to store
    yield call(setPayerInfoSaga, payload);
}

/**
 * auto subscribe user if needed by amazon payment
 */
export function* amazonAutoSubscription(payerInfoData: PayerInfoType): SagaIterator {
    if (payerInfoData.method === PAYMENTS.METHOD_AMAZON_V2) {
        yield putResolve(getReviewData());
        const customerData = yield select(getCustomerAddresses);
        const shouldBeSubbed = yield call(shouldBeAutoSubscribed, customerData.email, {
            admin_area_1: customerData.regionCode,
            country_code: customerData.country,
        });
        const {search} = apiConfig.hostStore.getState().router.location;

        managerNewsletterSubscription.setEnabledForLocation(shouldBeSubbed);

        if (shouldBeSubbed) {
            apiConfig.hostStore.dispatch(
                subscribe(customerData.email, {
                    affId: getAffId({search}),
                    country: customerData.country,
                    from: SUBSCRIPTION_LOCATION.checkout,
                    state: customerData.regionCode,
                    subscriptionMethod: 'Auto',
                    verifyRealEmail: false,
                }),
            );
            localStore.set('email', customerData.email);
        } else if (
            !shouldBeSubbed &&
            managerNewsletterSubscription.getSubscriptionStatus() === 'UNKNOWN'
        ) {
            yield put(createTransitional(customerData.email));
        }
    }
}

/**
 * Send payment info to store for render completed step
 */
export function* saveAndSetPaymentMethod({payload}: ActionType<PayerInfoType>): SagaIterator {
    // begin pending order
    yield put(completeOrderReviewPending());

    yield call(setLoadingSavePaymentSaga, true);

    yield call(sendPaymentInfoSaga, payload);

    // amazon autoSubscription
    yield call(amazonAutoSubscription, payload);

    // place order
    yield put(placeOrder());
}

/**
 * Fetch Amazon session info and set to store
 */
export function* fetchAndSetAmazonSessionInfo({payload}: ActionType<string>): SagaIterator {
    const fetchSessionInfoAction = yield call(fetchAmazonSessionInfo, payload);

    yield putResolve(fetchSessionInfoAction);
}
